<script setup lang="ts">
import { useRouter } from "vue-router";
//import Lucide from "../../base-components/Lucide";
import SideMenuTooltip from "./SideMenuTooltip.vue";
import { FormattedMenu, linkTo } from "./main-menu";
import {checkTypeOfIcon, substractNameOfIcon} from "@wpmss/ts-icons-helper"

interface MenuProps {
  class?: string | object;
  menu: FormattedMenu;
  layout: "rubick" | "enigma" | "icewall" | "tinker";
  isRoot: boolean;
  position: "side" | "simple";
}

const router = useRouter();
// @ts-ignore
const props = defineProps<MenuProps>();
</script>

<template>
  <SideMenuTooltip
    :key="props.menu.title"
    as="a"
    :content="$t(props.menu.title)"
    :position="position"
    :href="
      props.menu.subMenu && props.menu.subMenu.length > 0
        ? 'javascript:;'
        : 'management/' + props.menu.href
    "
    :class="[
      {
        'h-[50px] flex items-center pl-5 text-white mb-1 relative rounded-full':
          layout === 'rubick',
        'dark:text-slate-300':
          props.menu.active && !isRoot && layout === 'rubick',
        'text-white/70 dark:text-slate-400':
          !props.menu.active && !isRoot && layout === 'rubick',
        'z-10 bg-slate-100 dark:bg-darkmode-700':
          props.menu.active && isRoot && layout === 'rubick',
        'before:content-[\'\'] before:w-[30px] before:h-[30px] before:-mt-[30px] before:rotate-90 before:scale-[1.04] before:bg-[length:100%] before:bg-menu-corner before:absolute before:top-0 before:right-0 before:-mr-5 dark:before:bg-menu-corner-dark':
          props.menu.active && isRoot && layout === 'rubick',
        'after:content-[\'\'] after:w-[30px] after:h-[30px] after:mt-[50px] after:scale-[1.04] after:bg-[length:100%] after:bg-menu-corner after:absolute after:top-0 after:right-0 after:-mr-5 dark:after:bg-menu-corner-dark':
          props.menu.active && isRoot && layout === 'rubick',
        '[&>div:nth-child(1)]:hover:before:bg-white/5 [&>div:nth-child(1)]:hover:before:dark:bg-darkmode-500/70':
          !props.menu.active &&
          !props.menu.activeDropdown &&
          isRoot &&
          layout === 'rubick',
      },
      {
        'h-[50px] flex items-center pl-5 text-slate-600 mb-1 relative rounded-xl dark:text-slate-300':
          layout === 'enigma',
        'text-slate-600 dark:text-slate-400':
          !props.menu.active && props.level != 'first' && layout === 'enigma',
        'bg-slate-100 dark:bg-transparent':
          props.menu.active && isRoot && layout === 'enigma',
        'before:content-[\'\'] before:block before:inset-0 before:rounded-xl before:absolute before:border-b-[3px] before:border-solid before:border-black/[0.08] before:dark:border-black/[0.08]':
          props.menu.active && isRoot && layout === 'enigma',
        'after:content-[\'\'] after:w-[20px] after:h-[80px] after:mr-[-27px] after:bg-menu-active after:bg-no-repeat after:bg-cover after:absolute after:top-0 after:bottom-0 after:right-0 after:my-auto after:dark:bg-menu-active-dark':
          props.menu.active && isRoot && layout === 'enigma',
        'hover:bg-slate-100 hover:dark:bg-transparent hover:before:content-[\'\'] hover:before:block hover:before:inset-0 hover:before:rounded-xl hover:before:absolute hover:before:z-[-1] hover:before:border-b-[3px] hover:before:border-solid hover:before:border-black/[0.08] hover:before:dark:bg-darkmode-700':
          !props.menu.active &&
          !props.menu.activeDropdown &&
          isRoot &&
          layout === 'enigma',
        // Animation
        'after:-mr-[47px] after:opacity-0 after:animate-[0.4s_ease-in-out_0.1s_active-side-menu-chevron] after:animate-fill-mode-forwards':
          props.menu.active && isRoot && layout === 'enigma',
      },
      {
        'h-[50px] flex items-center pl-5 text-white mb-1 relative rounded-lg':
          layout === 'icewall',
        'dark:text-slate-300':
          props.menu.active && !isRoot && layout === 'icewall',
        'text-white/70 dark:text-slate-400':
          !props.menu.active && !isRoot && layout === 'icewall',
        'bg-primary dark:bg-transparent':
          props.menu.active && isRoot && layout === 'icewall',
        'before:content-[\'\'] before:block before:inset-0 before:bg-white/[0.08] before:rounded-lg before:absolute before:border-b-[3px] before:border-solid before:border-black/10 before:dark:border-black/10':
          props.menu.active && isRoot && layout === 'icewall',
        'after:content-[\'\'] after:w-[20px] after:h-[80px] after:mr-[-27px] after:bg-no-repeat after:bg-cover after:absolute after:top-0 after:bottom-0 after:right-0 after:my-auto after:bg-menu-active dark:after:bg-menu-active-dark':
          props.menu.active && isRoot && layout === 'icewall',
        'hover:bg-primary/60 hover:dark:bg-transparent hover:before:block hover:before:inset-0 hover:before:bg-white/[0.04] hover:before:rounded-lg hover:before:absolute hover:before:z-[-1] hover:before:dark:bg-darkmode-700':
          !props.menu.active &&
          !props.menu.activeDropdown &&
          isRoot &&
          layout === 'icewall',
        // Animation
        'after:mr-[-47px] after:opacity-0 after:animate-[0.3s_ease-in-out_1s_active-side-menu-chevron] after:animate-fill-mode-forwards':
          props.menu.active && isRoot && layout === 'icewall',
      },
      {
        'h-[50px] flex items-center pl-5 text-white mb-1 relative rounded-xl':
          layout === 'tinker',
        'dark:text-slate-300':
          props.menu.active && !isRoot && layout === 'tinker',
        'text-white/70 dark:text-slate-400':
          !props.menu.active && !isRoot && layout === 'tinker',
        'bg-primary dark:bg-transparent':
          props.menu.active && isRoot && layout === 'tinker',
        'before:content-[\'\'] before:block before:inset-0 before:bg-white/[0.08] before:rounded-xl before:absolute before:border-b-[3px] before:border-solid before:border-black/10 before:dark:border-black/10 before:dark:bg-darkmode-700':
          props.menu.active && isRoot && layout === 'tinker',
        'hover:bg-primary/60 hover:dark:bg-transparent hover:before:block hover:before:inset-0 hover:before:bg-white/[0.04] hover:before:rounded-xl hover:before:absolute hover:before:z-[-1] hover:before:dark:bg-darkmode-700':
          !props.menu.active &&
          !props.menu.activeDropdown &&
          isRoot &&
          layout === 'tinker',
      },
      props.class,
    ]"
    @click="linkTo(props.menu, router, $event)"
  >
    <div
      :class="[
        {
          'text-primary dark:text-slate-300':
            props.menu.active && isRoot && layout === 'rubick',
          'dark:text-slate-400':
            !props.menu.active && isRoot && layout === 'rubick',
          'before:content-[\'\'] before:z-[-1] before:absolute before:top-0 before:right-0 before:-mr-5 before:w-11 before:h-full before:bg-slate-100 before:dark:bg-darkmode-700':
            props.menu.active && isRoot && layout === 'rubick',
          'before:content-[\'\'] before:z-[-1] before:w-[230px] before:absolute before:top-0 before:left-0 before:h-full before:rounded-l-full before:transition before:ease-in before:duration-100':
            !props.menu.activeDropdown &&
            !props.menu.active &&
            isRoot &&
            layout === 'rubick',
        },
        {
          'text-primary z-10 dark:text-slate-300':
            props.menu.active && isRoot && layout === 'enigma',
          'text-slate-700': props.menu.active && !isRoot && layout === 'enigma',
        },
        {
          'z-10 dark:text-slate-300':
            props.menu.active && isRoot && layout === 'icewall',
          'dark:text-slate-400':
            !props.menu.active && isRoot && layout === 'icewall',
        },
        {
          'z-10 dark:text-slate-300':
            props.menu.active && isRoot && layout === 'tinker',
          'dark:text-slate-400':
            !props.menu.active && isRoot && layout === 'tinker',
        },
      ]"
    >
      <!--Menu icon here-->
      <div v-if="props.menu.menu_icon != 'no icon'">
      <font-awesome-icon class="h-7"
        v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'FontAwesome'"
        :icon="substractNameOfIcon(props.menu.menu_icon)"
      />
      
      <Lucide  v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'Lucide'" :icon="props.menu.menu_icon" class="" />
      <component v-if="checkTypeOfIcon(props.menu.rec_menu_icon_parent) == 'HeroIcon'" :is="props.menu.menu_icon" class="w-9" />
      </div>
    </div>
    <div
      v-if="position === 'side'"
      :class="[
        {
          'hidden xl:flex items-center w-full ml-3': layout === 'rubick',
          'font-medium':
            props.menu.active && props.level != 'first' && layout === 'rubick',
          'text-slate-800 font-medium dark:text-slate-300':
            props.menu.active && isRoot && layout === 'rubick',
          'dark:text-slate-400':
            !props.menu.active && isRoot && layout === 'rubick',
        },
        {
          'w-full ml-3 hidden xl:flex items-center': layout === 'enigma',
          'text-primary font-medium z-10 dark:text-slate-300':
            props.menu.active && isRoot && layout === 'enigma',
          'text-slate-700 font-medium dark:text-slate-300':
            props.menu.active && !isRoot && layout === 'enigma',
          'dark:text-slate-400': !props.menu.active && layout === 'enigma',
        },
        {
          'hidden xl:flex items-center w-full ml-3': layout === 'icewall',
          'font-medium':
            props.menu.active && props.level != 'first' && layout === 'icewall',
          'font-medium z-10 dark:text-slate-300':
            props.menu.active && props.level == 'first' && layout === 'icewall',
          'dark:text-slate-400':
            !props.menu.active &&
            props.level == 'first' &&
            layout === 'icewall',
        },
        {
          'hidden xl:flex w-full ml-3 items-center': layout === 'tinker',
          'font-medium': props.menu.active && !isRoot && layout === 'tinker',

          'font-medium z-10 dark:text-slate-300':
            props.menu.active && isRoot && layout === 'tinker',

          'dark:text-slate-400':
            !props.menu.active && isRoot && layout === 'tinker',
        },
      ]"
    >
      {{ $t(props.menu.title) }}
      <div
        v-if="props.menu.subMenu && props.menu.subMenu.length > 0"
        :class="[
          'transition ease-in duration-100 ml-auto mr-6 hidden xl:block',
          { 'transform rotate-180': props.menu.activeDropdown },
        ]"
      >
        <Lucide class="w-4 h-4" icon="ChevronDown" />
      </div>
    </div>
  </SideMenuTooltip>
</template>
